// ════════════════════════════════════════════════════════════════════════════
// 🚨 ConfirmModal — Phase 1.4 Replacement for window.confirm()
// ════════════════════════════════════════════════════════════════════════════
// Centered modal dialog with dimmed background, dual-button confirmation.
// Used for ALL destructive actions per UX research §Error Correction Mechanics.
//
// Usage (imperative — no provider needed):
//   const ok = await confirmAction({
//     title: 'Remove cashier?',
//     message: 'This action cannot be undone.',
//     confirmLabel: 'Remove',
//     destructive: true,
//   });
//   if (!ok) return;
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** Optional manager PIN gate — when set, user must type this exact value to confirm. */
  requirePin?: string;
}

type Resolver = (ok: boolean) => void;

let resolverRef: Resolver | null = null;
let setStateRef: ((next: { open: boolean; opts: ConfirmOptions | null }) => void) | null = null;

/** Imperative API. Renders the singleton modal & resolves on user action. */
export function confirmAction(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    resolverRef = resolve;
    if (setStateRef) setStateRef({ open: true, opts });
  });
}

/**
 * Mount this ONCE near the app root (or inside POSDashboard root).
 * Multiple mounts are safe but only the latest wins for resolution.
 */
export const ConfirmModalHost: React.FC = () => {
  const [state, setState] = useState<{ open: boolean; opts: ConfirmOptions | null }>({ open: false, opts: null });
  const [pinInput, setPinInput] = useState('');

  useEffect(() => {
    setStateRef = setState;
    return () => { if (setStateRef === setState) setStateRef = null; };
  }, []);

  useEffect(() => {
    if (!state.open) setPinInput('');
  }, [state.open]);

  const close = (ok: boolean) => {
    if (resolverRef) { resolverRef(ok); resolverRef = null; }
    setState({ open: false, opts: null });
  };

  // Esc key cancels
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter' && !state.opts?.requirePin) close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.open]);

  if (!state.open || !state.opts) return null;

  const { title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive, requirePin } = state.opts;
  const pinValid = !requirePin || pinInput === requirePin;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="confirm-bg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) close(false); }}
      >
        <motion.div
          key="confirm-card"
          initial={{ scale: 0.95, y: 12, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 12, opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start gap-3 px-5 pt-5 pb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              destructive ? 'bg-rose-50 border border-rose-200' : 'bg-brand-surface border border-brand-surface-border'
            }`}>
              <AlertTriangle size={18} className={destructive ? 'text-rose-500' : 'text-brand-primary'} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[14px] font-black text-slate-800 leading-tight">{title}</h3>
              {message && <p className="text-[12px] text-slate-500 mt-1 leading-snug">{message}</p>}
            </div>
            <button
              onClick={() => close(false)}
              className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors shrink-0"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          {/* Optional PIN gate */}
          {requirePin && (
            <div className="px-5 pb-3">
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Manager PIN</label>
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && pinValid) close(true); }}
                placeholder="••••"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[14px] font-mono tracking-widest text-center"
              />
              {pinInput.length > 0 && !pinValid && (
                <p className="text-[10px] text-rose-500 mt-1.5">Incorrect PIN</p>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2 px-5 pb-5 pt-2">
            <button
              onClick={() => close(false)}
              className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-[12px] font-bold uppercase tracking-wider transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              onClick={() => close(true)}
              disabled={!pinValid}
              className={`flex-1 py-2.5 rounded-xl text-[12px] font-bold uppercase tracking-wider transition-all active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed ${
                destructive
                  ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-md shadow-rose-500/20'
                  : 'bg-brand-primary hover:opacity-90 text-brand-text-on-primary shadow-md shadow-brand-primary/20'
              }`}
            >
              {confirmLabel}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
